<script setup lang="ts">
import { JOB_PACK_DAYS } from '~/constants/pricing'

/**
 * The way to buy an interview prep preview, pinned in a dialog's footer so it
 * stays in sight while the preview scrolls: one green button for this prep.
 * The Job Search Pack is offered here only where this prep is not on sale on
 * its own.
 */
withDefaults(defineProps<{
  /** This prep can be bought on its own; otherwise only the pack is offered. */
  canBuySingle: boolean
  busy?: boolean
  error?: boolean
}>(), {
  busy: false,
  error: false,
})

const emit = defineEmits<{
  'buy-single': []
  'buy-pack': []
}>()

const { t } = useI18n()
const { interviewPrepPrice } = usePremiumPlans()
/** Less what was paid for this CV before, as the checkout charges it (usePackCredit). */
const { packPrice, creditNote } = usePackCredit()
</script>

<template>
  <div class="mx-auto max-w-md">
    <SofaxBtn
      v-if="canBuySingle"
      variant="accent"
      block
      class="buy-bar-btn"
      :disabled="busy"
      :label="t('interviewPrep.editor.buySingle', { price: interviewPrepPrice })"
      @click="emit('buy-single')"
    />
    <SofaxBtn
      v-else
      variant="dark"
      block
      class="buy-bar-btn"
      :disabled="busy"
      :label="t('interviewPrep.editor.buyAll', { price: packPrice })"
      @click="emit('buy-pack')"
    />
    <p class="mt-2 hidden text-center !text-xs text-muted sm:block dark:text-gray-400">
      {{ canBuySingle ? t('interviewPrep.offer.note') : t('interviewPrep.unlock.packText', { days: JOB_PACK_DAYS }) }}
    </p>
    <p v-if="!canBuySingle && creditNote" class="mt-1.5 text-center !text-xs font-semibold text-ink dark:text-white">{{ creditNote }}</p>
    <p v-if="error" class="mt-1.5 text-center !text-xs !text-red-600 dark:!text-red-400">{{ t('premium.checkoutError') }}</p>
  </div>
</template>

<style scoped>
/* A phone keeps the bar to one slim row. */
@media (max-width: 639px) {
  .buy-bar-btn {
    @apply !min-w-0 !px-4 !py-2.5 !text-sm;
  }
}

@media (min-width: 640px) {
  .buy-bar-btn {
    @apply !py-3;
  }
}
</style>
